import {AnalyticEventType, AnalyticEventCategory, FeedbackFormLocationEnum, ReferralTypeEnum, DiscordChannelTypeEnum} from './entity_datas/EntityDatas'
import {StoreIconType} from './entity_datas/StoreEnums'
import {RankedModeTypes, RankedSeasonTypes} from './GameEnums'
import {RankedDivisionTypes, RankedSubDivisionTypes} from './leaderboard_data/LeaderboardEnums'
import {ExistingFriendInterface} from './friends/FriendOnlineStatus'
import {BaseRankedUserState, CountryCode, ContinentCode, RegionCode} from '@colonist/leaderboard-types'
import {GameData, MatchmakingMatchTypes, UserReportReasonEnum} from '@colonist/game-base-types'
import {HTTPStatus} from './errors/ErrorEnums'
import {TranslationObject} from '@colonist/i18n'
import {LeaderboardTabName} from './leaderboard_data/LeaderboardInterfaces'
import {BigId, ColonistMembershipType, DiscordSnowflake, LobbyImage} from '@colonist/types'
import {PlayerColor} from '@colonist/game-states'
import {UserStateData} from './UserStateData'
import {ABTestCategory, ClientABTests} from './ABTestTypes.ts'

export module AjaxDataTypes {
    export interface AnalyticData {
        type: AnalyticEventType
        category: AnalyticEventCategory
        floatValue?: number | undefined
        stringValue?: string | undefined
        dimensions?: Record<string, any> | undefined
    }

    export interface PurchaseItemRequestStatus {
        status: boolean
        message: TranslationObject
    }

    export interface StripeCheckoutRequestStatus {
        status: boolean
        message: TranslationObject
        session: any
    }

    export function isIStripeCheckoutRequestStatus(object: any): object is StripeCheckoutRequestStatus {
        return typeof object.status == 'boolean' && typeof object.message == 'string'
    }

    export class PublicProfileDataStickyPlayerInfo {
        username: string = ''
        iconImageId: LobbyImage | undefined
        isLoggedIn: boolean
        country: string | undefined
        currentMembershipType: ColonistMembershipType | undefined
    }

    export class PublicProfileDataOverview {
        userId: BigId
        username: string = ''
        karma: string = ''
        gameData: PublicProfileGameDataOverview = new PublicProfileGameDataOverview()
    }

    export class PublicProfileGameDataOverview {
        pointsInLast100Games: number = 0
        totalGames: number = 0
        winPercent: string = '0'
        pointsPerGame: string = '0'
    }

    export class UserFriendsData {
        friends: ExistingFriendInterface[] = []
        friendRequestsSent: string[] = []
        friendRequestsReceived: string[] = []
    }

    export class FriendRequestReceivedData {
        username: string
        dateCreated: Date
    }

    export interface MessageResponseData {
        status: HTTPStatus
        message?: string
    }

    export interface UsernameData {
        username: string
        shouldPurchaseIfNoAttemptsLeft?: boolean
    }

    export interface IconData {
        icon: StoreIconType
    }

    export interface PreferredColorData {
        preferredColor: PlayerColor
    }

    export interface FriendChangeData {
        username: string
    }

    export interface UserReportData {
        reason: UserReportReasonEnum
        databaseGameId?: BigId
    }

    export interface FeedbackFormData {
        location: FeedbackFormLocationEnum
        gameId: string | undefined
        skipped: boolean
        rate: number | undefined
        email: string | undefined
        feedback: string | undefined
    }

    export interface FeedbackFormShouldShowData {
        shouldShow: boolean
        lastShown: Date | undefined
    }

    export interface FreePlayDiceRollData {
        diceRoll: number | undefined
    }

    export interface ProfileDataRankedSeasonDropdown {
        name: TranslationObject
        seasonType: RankedSeasonTypes
    }

    export interface ProfileDataRanked {
        modeName: string
        modeType: RankedModeTypes
        division: RankedDivisionTypes
        subdivision: RankedSubDivisionTypes
        winRate: string
        gamesPlayed: string
        skillRating: string
        matchmakingRedirectType: MatchmakingMatchTypes
        rankedStanding: ProfileDataRankedStanding[]
        currentRankedState: BaseRankedUserState | undefined
    }

    export interface ProfileDataRankedStanding {
        name: LeaderboardTabName
        rank: string
    }

    export interface DiscordAccessTokenCode {
        code: string
    }

    export interface DiscordAccessTokenData {
        accessToken: string
        csrfToken: string
    }

    export interface DiscordData {
        userData: DiscordUserData
        currentServerId: DiscordSnowflake | undefined
        serversData: DiscordServerData[]
        channelTypeId: DiscordChannelTypeEnum | undefined
    }

    export interface DiscordUserData {
        discordUserId: DiscordSnowflake
        username: string
        locale?: string | undefined
        verified?: boolean | undefined
        email?: string | undefined
        flags?: number | undefined
        premiumType?: number | undefined
        publicFlags?: number | undefined
        serverCount: number
        joinedServers: DiscordSnowflake[]
        ownedServers: DiscordSnowflake[]
    }

    export interface DiscordServerData {
        discordServerId: DiscordSnowflake
        name: string
        owner?: boolean | undefined
        memberCount?: number | undefined
        presenceCount?: number | undefined
        preferredLocale?: string | undefined
        vanityUrl?: string | undefined
    }

    export interface DiscordPurchaseData {
        accessToken: string
        giftMetadataId: BigId | undefined
    }

    export interface LanguageChangeData {
        language: string
    }

    export interface ReferrerData {
        referredByUserId: BigId | undefined
        referredByDiscordUserId: DiscordSnowflake | undefined
        referralTypeId: ReferralTypeEnum
    }

    export interface AssignABTestData {
        abTestCategory: ABTestCategory
    }

    export interface DiscordProfileData {
        id: string
        username: string
        avatar: string
        email?: string
    }

    export interface DeleteUserData {
        username: string
    }

    export interface UsernameValidationData {
        isAvailable: boolean
        containsToxicWord: boolean
        suggestedPostfixes: string[]
    }

    export interface ChangeRegionData {
        continentCode: ContinentCode | undefined
        countryCode: CountryCode | undefined
        stateCode: RegionCode | undefined
    }

    export interface ActivateGiftMembershipData {
        itemId: BigId
    }

    export interface ProfileHistoryData {
        profileUserId: BigId
        gameDatas: GameData[]
    }

    export interface UserStateResponse {
        userState: UserStateData | undefined
        csrfToken: string
        abTests: ClientABTests
    }

    export interface LastDeviceSessionData {
        lastCompletedDeviceSessionTimestamp: string | undefined
    }

    export interface UsernameResponse {
        username: string
    }

    export interface Region {
        continentCode: ContinentCode
        countryCode: CountryCode
        regionCode: RegionCode
        regionName: string
    }

    export interface Regions {
        regions: Region[]
    }
}
